import { useQuery, useQueryClient } from "@tanstack/react-query";
import { OrderDeliveryAddress } from "../../core/models/entities/Order";
import { Profile } from "../../core/models/entities/Profile";
import { Resource } from "../../core/models/wrappers/Resource";
import { useApiClient } from "../clients/ApiClient";
import {
  mapProfileToUpdateProfileDto,
  mapToProfile,
} from "../models/mappers/mapProfile";
import { OrderDeliveryAddressDto } from "../models/responses/OrderDto";

export interface ProfileRepository {
  useProfile: (orgId?: number) => Resource<Profile>;
  useAddresses: (orgId?: number) => Resource<OrderDeliveryAddress[]>;
  setAddessAsDefault: (orgId: number, addressId: number) => Promise<void>;
  updateProfile: (profile: Profile) => Promise<Profile>;
  updateProfileById: (orgId: number, profile: Profile) => Promise<Profile>;
  clearProfile: () => void;
}

export function useProfileRepository(): ProfileRepository {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  async function invalidateProfileQuery(orgId?: number) {
    await queryClient.refetchQueries({
      queryKey: ["profile", orgId],
      type: "all",
      refetchPage: () => true,
    });
  }

  async function invalidateAddressQuery() {
    await queryClient.refetchQueries({
      queryKey: ["addresses"],
      type: "all",
      refetchPage: () => true,
    });
  }

  function useProfile(orgId?: number): Resource<Profile> {
    return useQuery(
      ["profile", orgId],
      () => apiClient.getProfile(orgId).then((data) => mapToProfile(data)),
      {
        staleTime: 600000,
      }
    );
  }

  function useAddresses(orgId?: number): Resource<OrderDeliveryAddress[]> {
    return useQuery(
      ["addresses", orgId],
      () =>
        apiClient
          .getDeliveryAddresses(orgId)
          .then((data) =>
            data?.map((d: OrderDeliveryAddressDto) => d as OrderDeliveryAddress)
          ) ?? [],
      {
        enabled: orgId != null,
      }
    );
  }

  async function setAddessAsDefault(orgId: number, addressId: number) {
    await apiClient.setDefaultDeliveryAddress(orgId, addressId);
    await invalidateAddressQuery();
  }

  async function updateProfile(profile: Profile): Promise<Profile> {
    let updateProfile = mapProfileToUpdateProfileDto(profile);
    const result = await apiClient.updateProfile(updateProfile);
    await invalidateProfileQuery();
    return mapToProfile(result);
  }

  async function updateProfileById(
    orgId: number,
    profile: Profile
  ): Promise<Profile> {
    let updateProfile = mapProfileToUpdateProfileDto(profile);
    const result = await apiClient.updateProfile(updateProfile, orgId);
    await invalidateProfileQuery();
    return mapToProfile(result);
  }

  async function clearProfile() {
    await invalidateProfileQuery();
  }

  return {
    useProfile,
    useAddresses,
    setAddessAsDefault,
    updateProfile,
    updateProfileById,
    clearProfile,
  };
}
